import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  FiSearch, FiPlus, FiEdit2, FiTrash2, FiX,
  FiUsers, FiSave, FiAlertTriangle, FiFileText, FiChevronDown, FiExternalLink
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { buyerDetailAPI, proformaAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const emptyBuyer = {
  buyer_name: '',
  buyer_address: '',
  details: '',
  email: '',
  phone_no: '',
};

// ── Styles ──────────────────────────────────────────────────────────────────────
const styles = {
  container: {
    fontFamily: 'Inter, sans-serif',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 28,
    flexWrap: 'wrap',
    gap: 16,
  },
  title: {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 28,
    fontWeight: 800,
    color: '#064e3b',
    margin: 0,
  },
  subtitle: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 4,
  },
  searchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 24,
    flexWrap: 'wrap',
  },
  searchWrapper: {
    position: 'relative',
    flex: 1,
    minWidth: 250,
  },
  searchIcon: {
    position: 'absolute',
    left: 14,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#059669',
  },
  searchInput: {
    width: '100%',
    padding: '12px 14px 12px 44px',
    border: '2px solid rgba(16, 185, 129, 0.2)',
    borderRadius: 12,
    fontSize: 14,
    outline: 'none',
    background: 'rgba(255,255,255,0.8)',
    backdropFilter: 'blur(8px)',
    transition: 'all 0.2s ease',
    fontFamily: 'Inter, sans-serif',
    boxSizing: 'border-box',
  },
  addBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #059669, #10b981)',
    color: 'white',
    border: 'none',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
    fontFamily: 'Poppins, sans-serif',
    whiteSpace: 'nowrap',
  },
  glassCard: {
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(20px)',
    borderRadius: 20,
    border: '1px solid rgba(255, 255, 255, 0.5)',
    boxShadow: '0 8px 32px rgba(6, 78, 59, 0.08)',
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
  },
  th: {
    padding: '14px 16px',
    textAlign: 'left',
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    color: '#047857',
    background: 'rgba(240, 253, 244, 0.8)',
    borderBottom: '2px solid rgba(16, 185, 129, 0.15)',
  },
  td: {
    padding: '14px 16px',
    fontSize: 14,
    color: '#1f2937',
    borderBottom: '1px solid rgba(16, 185, 129, 0.08)',
    verticalAlign: 'middle',
  },
  actionBtn: {
    width: 34,
    height: 34,
    borderRadius: 8,
    border: 'none',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    marginRight: 6,
  },
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(6, 78, 59, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: 'white',
    borderRadius: 24,
    padding: '32px',
    width: '95%',
    maxWidth: 640,
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 25px 80px rgba(0, 0, 0, 0.2)',
    position: 'relative',
  },
  modalTitle: {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 22,
    fontWeight: 700,
    color: '#064e3b',
    marginBottom: 24,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  fieldLabel: {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: '#047857',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  fieldInput: {
    width: '100%',
    padding: '10px 14px',
    border: '2px solid rgba(16, 185, 129, 0.2)',
    borderRadius: 10,
    fontSize: 14,
    outline: 'none',
    fontFamily: 'Inter, sans-serif',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    boxSizing: 'border-box',
  },
  fieldTextarea: {
    width: '100%',
    padding: '10px 14px',
    border: '2px solid rgba(16, 185, 129, 0.2)',
    borderRadius: 10,
    fontSize: 14,
    outline: 'none',
    fontFamily: 'Inter, sans-serif',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    resize: 'vertical',
    minHeight: 80,
    boxSizing: 'border-box',
  },
  saveBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 28px',
    background: 'linear-gradient(135deg, #059669, #10b981)',
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
    fontFamily: 'Poppins, sans-serif',
  },
  cancelBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 28px',
    background: '#f3f4f6',
    color: '#6b7280',
    border: 'none',
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'Poppins, sans-serif',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  emptyIcon: {
    width: 72,
    height: 72,
    borderRadius: '50%',
    background: '#ecfdf5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
  },
  badge: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 9999,
    fontSize: 11,
    fontWeight: 600,
    background: '#ecfdf5',
    color: '#059669',
    border: '1px solid rgba(16, 185, 129, 0.2)',
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: 'linear-gradient(135deg, #059669, #10b981)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 14,
    fontWeight: 700,
    flexShrink: 0,
  },
};

const onFocusInput = (e) => {
  e.target.style.borderColor = '#10b981';
  e.target.style.boxShadow = '0 0 0 3px rgba(16,185,129,0.12)';
};
const onBlurInput = (e) => {
  e.target.style.borderColor = 'rgba(16,185,129,0.2)';
  e.target.style.boxShadow = 'none';
};

// ── Component ────────────────────────────────────────────────────────────────────
export default function BuyerManagement() {
  const { activeCompany } = useAuth();

  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingBuyer, setEditingBuyer] = useState(null);
  const [form, setForm] = useState({ ...emptyBuyer });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [expandedBuyerId, setExpandedBuyerId] = useState(null);
  const [buyerProformas, setBuyerProformas] = useState([]);
  const [loadingProformas, setLoadingProformas] = useState(false);
  const navigate = useNavigate();

  const toggleProformas = async (buyerId) => {
    if (expandedBuyerId === buyerId) {
      setExpandedBuyerId(null);
      setBuyerProformas([]);
      return;
    }
    setExpandedBuyerId(buyerId);
    setLoadingProformas(true);
    try {
      const res = await proformaAPI.getAll({ buyer_detail_id: buyerId });
      setBuyerProformas(Array.isArray(res.data) ? res.data : []);
    } catch {
      setBuyerProformas([]);
    } finally {
      setLoadingProformas(false);
    }
  };

  // ── Fetch buyers ─────────────────────────────────────────────────────────────
  const fetchBuyers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await buyerDetailAPI.getAll();
      setBuyers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load buyers');
      setBuyers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBuyers();
  }, [fetchBuyers, activeCompany]);

  // ── Filtered list ────────────────────────────────────────────────────────────
  const filtered = search.trim()
    ? buyers.filter((b) => {
        const q = search.toLowerCase();
        return (
          (b.buyer_name || '').toLowerCase().includes(q) ||
          (b.buyer_address || '').toLowerCase().includes(q) ||
          (b.details || '').toLowerCase().includes(q) ||
          (b.email || '').toLowerCase().includes(q) ||
          (b.phone_no || '').toLowerCase().includes(q)
        );
      })
    : buyers;

  // ── Open modal ───────────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditingBuyer(null);
    setForm({ ...emptyBuyer });
    setShowModal(true);
  };

  const openEdit = (buyer) => {
    setEditingBuyer(buyer);
    setForm({
      buyer_name: buyer.buyer_name || '',
      buyer_address: buyer.buyer_address || '',
      details: buyer.details || '',
      email: buyer.email || '',
      phone_no: buyer.phone_no || '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBuyer(null);
    setForm({ ...emptyBuyer });
  };

  // ── Save ─────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.buyer_name.trim()) {
      toast.warning('Buyer name is required');
      return;
    }
    if (!form.buyer_address.trim()) {
      toast.warning('Buyer address is required');
      return;
    }
    // Duplicate = same name AND same email AND same phone (same name alone is fine)
    const norm = (v) => (v || '').trim().toLowerCase();
    const duplicate = buyers.find(
      (b) =>
        norm(b.buyer_name) === norm(form.buyer_name) &&
        norm(b.email) === norm(form.email) &&
        norm(b.phone_no) === norm(form.phone_no) &&
        (!editingBuyer || b.id !== editingBuyer.id)
    );
    if (duplicate) {
      toast.error('A buyer with the same name, email and phone already exists');
      return;
    }
    setSaving(true);
    try {
      if (editingBuyer) {
        await buyerDetailAPI.update(editingBuyer.id, form);
        toast.success('Buyer updated');
      } else {
        await buyerDetailAPI.create(form);
        toast.success('Buyer created');
      }
      closeModal();
      fetchBuyers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save buyer');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    try {
      await buyerDetailAPI.delete(id);
      toast.success('Buyer deleted');
      setDeleteConfirm(null);
      fetchBuyers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete buyer');
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      style={styles.container}
    >
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Buyer Details</h1>
          <p style={styles.subtitle}>
            {activeCompany?.exporter_name || 'All Companies'} &mdash;{' '}
            {filtered.length} buyer{filtered.length !== 1 ? 's' : ''}
          </p>
        </div>
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={openAdd}
          style={styles.addBtn}
        >
          <FiPlus size={18} /> Add Buyer
        </motion.button>
      </div>

      {/* Search */}
      <div style={styles.searchRow}>
        <div style={styles.searchWrapper}>
          <FiSearch size={18} style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by name, address, email or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.searchInput}
            onFocus={onFocusInput}
            onBlur={onBlurInput}
          />
          {search && (
            <FiX
              size={16}
              style={{
                position: 'absolute', right: 14, top: '50%',
                transform: 'translateY(-50%)', color: '#94a3b8', cursor: 'pointer',
              }}
              onClick={() => setSearch('')}
            />
          )}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{
            width: 40, height: 40, border: '3px solid #d1fae5',
            borderTopColor: '#059669', borderRadius: '50%',
            animation: 'spin 0.8s linear infinite', margin: '0 auto 16px',
          }} />
          <span style={{ color: '#6b7280', fontSize: 14 }}>Loading buyers...</span>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : filtered.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>
            <FiUsers size={32} color="#059669" />
          </div>
          <h3 style={{ color: '#064e3b', marginBottom: 8, fontFamily: 'Poppins, sans-serif' }}>
            {search ? 'No Buyers Found' : 'No Buyers Yet'}
          </h3>
          <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 20 }}>
            {search
              ? 'No buyers match your search criteria.'
              : 'Add your first buyer to get started.'}
          </p>
        </div>
      ) : (
        <div style={styles.glassCard}>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, width: 50, textAlign: 'center' }}>##</th>
                  <th style={styles.th}>Buyer Name</th>
                  <th style={styles.th}>Address</th>
                  <th style={styles.th}>Details</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Phone</th>
                  <th style={{ ...styles.th, width: 120, textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {filtered.map((buyer, idx) => (
                    <React.Fragment key={buyer.id}>
                      <motion.tr
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15, delay: idx * 0.02 }}
                        style={{ cursor: 'pointer', transition: 'background 0.15s ease' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#f0fdf4'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        onClick={() => openEdit(buyer)}
                      >
                        <td style={{ ...styles.td, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>
                          {idx + 1}
                        </td>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={styles.avatar}>
                              {(buyer.buyer_name || '?')[0].toUpperCase()}
                            </div>
                            <span style={{ fontWeight: 600, color: '#064e3b' }}>
                              {buyer.buyer_name}
                            </span>
                          </div>
                        </td>
                        <td style={{ ...styles.td, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {buyer.buyer_address || '-'}
                        </td>
                        <td style={{ ...styles.td, fontSize: 12, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {buyer.details || '-'}
                        </td>
                        <td style={styles.td}>
                          {buyer.email || '-'}
                        </td>
                        <td style={styles.td}>
                          {buyer.phone_no || '-'}
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleProformas(buyer.id); }}
                            style={{
                              ...styles.actionBtn,
                              background: expandedBuyerId === buyer.id ? '#d1fae5' : '#f0fdf4',
                              color: '#059669',
                            }}
                            title="Show Proformas"
                          >
                            <FiFileText size={15} />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); openEdit(buyer); }}
                            style={{ ...styles.actionBtn, background: '#ecfdf5', color: '#059669' }}
                            title="Edit"
                          >
                            <FiEdit2 size={15} />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeleteConfirm(buyer); }}
                            style={{ ...styles.actionBtn, background: '#fef2f2', color: '#dc2626' }}
                            title="Delete"
                          >
                            <FiTrash2 size={15} />
                          </button>
                        </td>
                      </motion.tr>

                      {/* Expandable Proforma List */}
                      {expandedBuyerId === buyer.id && (
                        <motion.tr
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <td colSpan={6} style={{ padding: 0, background: '#f0fdf4', borderBottom: '2px solid rgba(16, 185, 129, 0.15)' }}>
                            <div style={{ padding: '16px 20px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                <FiFileText size={16} color="#059669" />
                                <span style={{ fontWeight: 700, fontSize: 13, color: '#064e3b', fontFamily: 'Poppins, sans-serif' }}>
                                  Proforma Invoices for {buyer.buyer_name}
                                </span>
                              </div>

                              {loadingProformas ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 0' }}>
                                  <div style={{
                                    width: 18, height: 18, border: '2px solid #d1fae5',
                                    borderTopColor: '#059669', borderRadius: '50%',
                                    animation: 'spin 0.8s linear infinite',
                                  }} />
                                  <span style={{ color: '#6b7280', fontSize: 13 }}>Loading proformas...</span>
                                </div>
                              ) : buyerProformas.length === 0 ? (
                                <p style={{ color: '#6b7280', fontSize: 13, margin: 0, padding: '8px 0' }}>
                                  No proforma invoices found for this buyer.
                                </p>
                              ) : (
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr>
                                      <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#047857', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid rgba(16,185,129,0.15)' }}>Invoice No</th>
                                      <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#047857', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid rgba(16,185,129,0.15)' }}>Date</th>
                                      <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#047857', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid rgba(16,185,129,0.15)' }}>Status</th>
                                      <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: 11, fontWeight: 700, color: '#047857', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid rgba(16,185,129,0.15)' }}>Total (USD)</th>
                                      <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#047857', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid rgba(16,185,129,0.15)', width: 60 }}></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {buyerProformas.map((pi) => (
                                      <tr
                                        key={pi.id}
                                        style={{ transition: 'background 0.15s ease', cursor: 'pointer' }}
                                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.6)'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                        onClick={() => navigate(`/proforma-invoices/${pi.id}`)}
                                      >
                                        <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 600, color: '#064e3b' }}>
                                          {pi.invoice_no}
                                        </td>
                                        <td style={{ padding: '10px 12px', fontSize: 13, color: '#374151' }}>
                                          {pi.document_date ? String(pi.document_date).slice(0, 10) : '-'}
                                        </td>
                                        <td style={{ padding: '10px 12px' }}>
                                          <span style={{
                                            display: 'inline-block',
                                            padding: '2px 10px',
                                            borderRadius: 9999,
                                            fontSize: 11,
                                            fontWeight: 600,
                                            background: pi.status === 'finalized' ? '#dbeafe' : pi.status === 'saved' ? '#ecfdf5' : '#fef9c3',
                                            color: pi.status === 'finalized' ? '#1d4ed8' : pi.status === 'saved' ? '#059669' : '#a16207',
                                            border: `1px solid ${pi.status === 'finalized' ? 'rgba(29,78,216,0.2)' : pi.status === 'saved' ? 'rgba(16,185,129,0.2)' : 'rgba(161,98,7,0.2)'}`,
                                            textTransform: 'capitalize',
                                          }}>
                                            {pi.status || 'draft'}
                                          </span>
                                        </td>
                                        <td style={{ padding: '10px 12px', fontSize: 13, color: '#374151', textAlign: 'right', fontWeight: 600 }}>
                                          {pi.total_usd != null ? `$${Number(pi.total_usd).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '-'}
                                        </td>
                                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); navigate(`/proforma-invoices/${pi.id}`); }}
                                            style={{ ...styles.actionBtn, background: 'transparent', color: '#059669', marginRight: 0 }}
                                            title="Open Proforma"
                                          >
                                            <FiExternalLink size={14} />
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </td>
                        </motion.tr>
                      )}
                    </React.Fragment>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Add/Edit Modal ──────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={styles.overlay}
            onClick={closeModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.25 }}
              style={styles.modal}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={styles.modalTitle}>
                {editingBuyer ? <FiEdit2 size={20} color="#059669" /> : <FiPlus size={20} color="#059669" />}
                {editingBuyer ? 'Edit Buyer' : 'Add Buyer'}
              </h2>

              {/* Buyer Name */}
              <div style={{ marginBottom: 16 }}>
                <label style={styles.fieldLabel}>
                  Buyer Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  value={form.buyer_name}
                  onChange={(e) => setForm({ ...form, buyer_name: e.target.value })}
                  placeholder="Enter buyer / consignee name"
                  style={styles.fieldInput}
                  onFocus={onFocusInput}
                  onBlur={onBlurInput}
                  autoFocus
                />
              </div>

              {/* Address */}
              <div style={{ marginBottom: 16 }}>
                <label style={styles.fieldLabel}>
                  Address <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <textarea
                  value={form.buyer_address}
                  onChange={(e) => setForm({ ...form, buyer_address: e.target.value })}
                  placeholder="Full buyer address (put IRC NO., VAT etc. in Details below)"
                  style={styles.fieldTextarea}
                  onFocus={onFocusInput}
                  onBlur={onBlurInput}
                  rows={3}
                />
              </div>

              {/* Details (IRC No., VAT etc.) */}
              <div style={{ marginBottom: 16 }}>
                <label style={styles.fieldLabel}>Details</label>
                <textarea
                  value={form.details}
                  onChange={(e) => setForm({ ...form, details: e.target.value })}
                  placeholder='e.g. IRC NO. 123456, VAT/BIN REG. NO. 78910'
                  style={styles.fieldTextarea}
                  onFocus={onFocusInput}
                  onBlur={onBlurInput}
                  rows={2}
                />
              </div>

              {/* Email & Phone */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
                <div>
                  <label style={styles.fieldLabel}>Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="buyer@example.com"
                    style={styles.fieldInput}
                    onFocus={onFocusInput}
                    onBlur={onBlurInput}
                  />
                </div>
                <div>
                  <label style={styles.fieldLabel}>Phone No (with Country Code)</label>
                  <input
                    type="text"
                    value={form.phone_no}
                    onChange={(e) => setForm({ ...form, phone_no: e.target.value })}
                    placeholder="+91-9876543210"
                    style={styles.fieldInput}
                    onFocus={onFocusInput}
                    onBlur={onBlurInput}
                  />
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  onClick={closeModal}
                  style={styles.cancelBtn}
                >
                  <FiX size={16} /> Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    ...styles.saveBtn,
                    opacity: saving ? 0.7 : 1,
                    cursor: saving ? 'wait' : 'pointer',
                  }}
                >
                  <FiSave size={16} /> {saving ? 'Saving...' : editingBuyer ? 'Update' : 'Save'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Delete Confirmation ──────────────────────────────────────────────────── */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={styles.overlay}
            onClick={() => setDeleteConfirm(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              style={{ ...styles.modal, maxWidth: 420, textAlign: 'center' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                background: '#fef2f2', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                <FiAlertTriangle size={28} color="#dc2626" />
              </div>
              <h3 style={{ fontFamily: 'Poppins, sans-serif', fontSize: 18, fontWeight: 700, color: '#064e3b', marginBottom: 8 }}>
                Delete Buyer?
              </h3>
              <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 24 }}>
                Are you sure you want to delete <strong>{deleteConfirm.buyer_name}</strong>?
                This cannot be undone.
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setDeleteConfirm(null)}
                  style={styles.cancelBtn}
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => handleDelete(deleteConfirm.id)}
                  style={{
                    ...styles.saveBtn,
                    background: 'linear-gradient(135deg, #dc2626, #ef4444)',
                    boxShadow: '0 4px 15px rgba(220, 38, 38, 0.3)',
                  }}
                >
                  <FiTrash2 size={16} /> Delete
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
